import DOMPurify from 'dompurify'
import { FC, useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import { Edit } from '@mui/icons-material'
import { Box, Button, Dialog, DialogActions, DialogContent, Skeleton, Typography } from '@mui/material'

import GRDialogTitle from '../../../components/GRDialogTitle/GRDialogTitle'

type AnalysisNoteViewDialogProps = {
  open: boolean
  title: string
  subtitle?: string
  notes?: string | null
  isLoading?: boolean
  isError?: boolean
  // Shown when there are no notes; without it the dialog is just empty.
  emptyText?: string
  onClose: () => void
  onEdit?: () => void
}

export const AnalysisNoteViewDialog: FC<AnalysisNoteViewDialogProps> = ({ open, title, subtitle, notes, isLoading, isError, emptyText, onClose, onEdit }) => {
  const { t } = useTranslation()
  const html = useMemo(() => DOMPurify.sanitize(notes ?? ''), [notes])
  const isReady = !isLoading && !isError

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <GRDialogTitle onClose={onClose}>
        <Box minWidth={0}>
          {title}
          {subtitle && (
            <Typography component="span" display="block" variant="body2" color="text.secondary">
              {subtitle}
            </Typography>
          )}
        </Box>
      </GRDialogTitle>
      <DialogContent>
        {isLoading ? (
          <Skeleton variant="rectangular" height={160} />
        ) : isError ? (
          <Typography color="error.main">{t('internal-analysts:analysis_notes_load_failed')}</Typography>
        ) : !notes && emptyText ? (
          <Typography color="text.secondary">{emptyText}</Typography>
        ) : (
          <Box data-testid="analysis-note" sx={{ '& p:first-of-type': { mt: 0 }, '& img': { maxWidth: '100%' } }} dangerouslySetInnerHTML={{ __html: html }} />
        )}
      </DialogContent>
      <DialogActions>
        {onEdit && isReady && (
          <Button startIcon={<Edit />} onClick={onEdit}>
            {t('internal-analysts:analysis_notes_edit')}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  )
}
